import {
  AccessTime,
  AttachMoney,
  AutoAwesome,
  Percent,
} from '@mui/icons-material'
import {
  LucideCalendarDays,
  LucideChartColumnIncreasing,
  LucidePackage,
  LucideScale,
  LucideTrendingUp,
} from 'lucide-react'
import React from 'react'

import useT from '@/i18n/useT'

export const COOLERS_LIST_TABLE_ID = 'coolers_list'
export const COOLER_PRODUCTS_LIST_TABLE_ID = 'cooler_products_list'

export const COOLERS_LIST_COLUMN_IDS = [
  'revenue',
  'profit',
  'compliance',
  'ai_insights',
  'last_snapshot',
] as const

export const COOLER_PRODUCTS_COLUMN_IDS = [
  'revenue',
  'margin',
  'volume',
  'facings',
  'revenue_per_facing',
  'average_volume_per_day',
  'days_supply',
] as const

export type CoolersListColumnId = (typeof COOLERS_LIST_COLUMN_IDS)[number]
export type CoolerProductsColumnId = (typeof COOLER_PRODUCTS_COLUMN_IDS)[number]
export type ManagedColumnId = CoolersListColumnId | CoolerProductsColumnId

export type TableColumnDefinition<TColumnId extends string = ManagedColumnId> =
  {
    id: TColumnId
    label: string
    icon?: React.ReactNode
  }

export const useCoolersListColumnDefinitions =
  (): TableColumnDefinition<CoolersListColumnId>[] => {
    const { t } = useT()

    return [
      {
        id: 'revenue',
        label: t('coolersTable.columns.revenue'),
        icon: <AttachMoney fontSize="small" />,
      },
      {
        id: 'profit',
        label: t('coolersTable.columns.profit'),
        icon: <LucideTrendingUp size={16} />,
      },
      {
        id: 'compliance',
        label: t('coolersTable.columns.compliance'),
        icon: <Percent fontSize="small" />,
      },
      {
        id: 'ai_insights',
        label: t('coolersTable.columns.aiInsights'),
        icon: <AutoAwesome fontSize="small" />,
      },
      {
        id: 'last_snapshot',
        label: t('coolersTable.columns.lastSnapshot'),
        icon: <AccessTime fontSize="small" />,
      },
    ]
  }

export const useCoolerProductsColumnDefinitions =
  (): TableColumnDefinition<CoolerProductsColumnId>[] => {
    const { t } = useT()

    return [
      {
        id: 'revenue',
        label: t('common.revenue'),
        icon: <AttachMoney fontSize="small" />,
      },
      {
        id: 'margin',
        label: t('common.margin'),
        icon: <LucideScale size={16} />,
      },
      {
        id: 'volume',
        label: t('common.volume'),
        icon: <LucideChartColumnIncreasing size={16} />,
      },
      {
        id: 'facings',
        label: t('common.facings'),
        icon: <LucidePackage size={16} />,
      },
      {
        id: 'revenue_per_facing',
        label: 'Rev/Facing',
        icon: <LucideTrendingUp size={16} />,
      },
      {
        id: 'average_volume_per_day',
        label: t('coolerProductStatsTable.columns.averageVolumePerDay'),
        icon: <LucideChartColumnIncreasing size={16} />,
      },
      {
        id: 'days_supply',
        label: t('coolerProductStatsTable.columns.daysSupply'),
        icon: <LucideCalendarDays size={16} />,
      },
    ]
  }
